import { ChangeEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import Loader from '../components/loader';
import { useData } from '../context/data.context';
import {
  CalibrationRecord,
  CalibrationReminder,
  ParametersType
} from '../constants/telosair-interfaces';
import {
  deleteCalibration,
  getCalibrationReminders,
  getCalibrations,
  uploadCalibrationFile
} from '../services/calibration.service';

const CSV_HEADER = 'serial_number,parameter,slope,intercept,effective_date,notes';

const SAMPLE_ROWS = [
  '4106,pm25_m,1.08,-0.4,2026-09-01,Co-located with reference monitor',
  '4106,co2,0.97,12.0,2026-09-01,'
];

/**
 * Formats an ISO date for display.
 * @param isoDate - ISO date string, or null.
 * @returns Formatted date, or an em dash.
 */
const formatDate = (isoDate: string | null): string =>
  isoDate
    ? new Date(isoDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

/**
 * Calibration settings: upload the coefficients that correct a sensor's
 * readings, see what is on record, and see which sensors are due.
 * @returns React element.
 */
export default function CalibrationDataPage(): ReactElement {
  const { devices, parameters } = useData();
  const [calibrations, setCalibrations] = useState<CalibrationRecord[]>([]);
  const [reminders, setReminders] = useState<CalibrationReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const [calibrationResponse, reminderResponse] = await Promise.all([
        getCalibrations(),
        getCalibrationReminders(true)
      ]);
      setCalibrations(calibrationResponse.data || []);
      setReminders(reminderResponse.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error('Could not load calibration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Uploads the chosen CSV and reports what was stored and what was skipped.
   * @param event - File input change event.
   */
  const handleUpload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setUploading(true);
      const { data } = await uploadCalibrationFile(file);
      if (data.applied > 0) {
        toast.success(
          `Applied ${data.applied} coefficient${data.applied !== 1 ? 's' : ''} across ${
            data.devices.length
          } sensor${data.devices.length !== 1 ? 's' : ''}`
        );
      }
      if (data.errors?.length) {
        // Every skipped row is surfaced: a silently dropped coefficient means
        // readings stay uncorrected without anyone noticing.
        data.errors.slice(0, 5).forEach((error) => toast.error(error));
        if (data.errors.length > 5) {
          toast.error(`${data.errors.length - 5} more rows were skipped`);
        }
      }
      if (data.applied === 0 && !data.errors?.length) {
        toast.info('The file had no rows to apply');
      }
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(e?.response?.data?.message || 'Could not read that file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        // Clear it so re-picking the same file fires another change event.
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Removes a coefficient after confirming, since readings change immediately.
   * @param record - Coefficient to remove.
   */
  const handleDelete = async (record: CalibrationRecord): Promise<void> => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Remove the ${record.parameter} calibration for sensor ${record.serialNumber}? ` +
        'Readings will immediately go back to their uncorrected values.'
    );
    if (!confirmed) {
      return;
    }
    try {
      setLoading(true);
      await deleteCalibration(record.id);
      await load();
      toast.success('Calibration removed');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error('Could not remove the calibration');
      setLoading(false);
    }
  };

  /**
   * Downloads a starter CSV with the expected header.
   */
  const downloadTemplate = (): void => {
    const blob = new Blob([[CSV_HEADER, ...SAMPLE_ROWS].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'calibration-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Device name for a serial number, falling back to the serial itself.
   * @param serialNumber - Sensor serial number.
   * @returns Display name.
   */
  const deviceName = (serialNumber: number): string =>
    devices.find((device) => device.serialNumber === serialNumber)?.deviceName ||
    String(serialNumber);

  /**
   * Human-readable parameter name.
   * @param parameter - Parameter key.
   * @returns Short name, or the key when the client has no such parameter.
   */
  const parameterName = (parameter: ParametersType): string =>
    parameters.find((item) => item.parameterName === parameter)?.shortName || parameter;

  return (
    <div className="my-4 flex flex-col gap-4 relative">
      {loading && <Loader />}

      <div className="rounded bg-white dark:bg-container-500 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Calibration</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-3xl">
          Upload the coefficients that correct a sensor&apos;s readings. Each row scales one
          parameter on one sensor as <code>value × slope + intercept</code>, and only applies to
          readings recorded on or after its effective date — data captured before the sensor was
          calibrated is left exactly as it came in. Nothing stored is rewritten, so removing a
          coefficient restores the original values immediately.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            id="calibration-file"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleUpload}
          />
          <Button
            theme="primary"
            size="md"
            name="Upload calibration CSV"
            loading={uploading}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          />
          <Button
            theme="secondary-gray"
            size="md"
            name="Download template"
            onClick={downloadTemplate}
          />
          <code className="text-xs text-gray-500 dark:text-gray-400">{CSV_HEADER}</code>
        </div>
      </div>

      <div className="rounded bg-white dark:bg-container-500 p-6">
        <h2 className="text-lg text-gray-900 dark:text-gray-300">Sensors due for calibration</h2>
        {reminders.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            No sensor has been running for a year without calibration.
          </p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-300">
              <thead className="text-xs uppercase text-gray-400 dark:text-gray-500">
                <tr>
                  <th className="py-2 pr-4">Sensor</th>
                  <th className="py-2 pr-4">Onboarded</th>
                  <th className="py-2 pr-4">Last calibrated</th>
                  <th className="py-2 pr-4">Due since</th>
                  <th className="py-2 pr-4">Reminder</th>
                </tr>
              </thead>
              <tbody>
                {reminders.map((reminder) => (
                  <tr key={reminder.serialNumber} className="border-t dark:border-t-gray-900">
                    <td className="py-2 pr-4">{reminder.deviceName || reminder.serialNumber}</td>
                    <td className="py-2 pr-4">{formatDate(reminder.onboardedDate)}</td>
                    <td className="py-2 pr-4">{formatDate(reminder.lastCalibratedDate)}</td>
                    <td className="py-2 pr-4">{formatDate(reminder.dueSince)}</td>
                    <td className="py-2 pr-4">
                      {reminder.snoozedUntil
                        ? `Hidden until ${formatDate(reminder.snoozedUntil)}`
                        : 'Showing'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded bg-white dark:bg-container-500 p-6">
        <h2 className="text-lg text-gray-900 dark:text-gray-300">Applied coefficients</h2>
        {calibrations.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            No calibration has been uploaded yet, so every reading is served as recorded.
          </p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-300">
              <thead className="text-xs uppercase text-gray-400 dark:text-gray-500">
                <tr>
                  <th className="py-2 pr-4">Sensor</th>
                  <th className="py-2 pr-4">Parameter</th>
                  <th className="py-2 pr-4">Slope</th>
                  <th className="py-2 pr-4">Intercept</th>
                  <th className="py-2 pr-4">Effective from</th>
                  <th className="py-2 pr-4">Notes</th>
                  <th className="py-2 pr-4" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {calibrations.map((record) => (
                  <tr key={record.id} className="border-t dark:border-t-gray-900">
                    <td className="py-2 pr-4">{deviceName(record.serialNumber)}</td>
                    <td className="py-2 pr-4">{parameterName(record.parameter)}</td>
                    <td className="py-2 pr-4">{record.slope}</td>
                    <td className="py-2 pr-4">{record.intercept}</td>
                    <td className="py-2 pr-4">{formatDate(record.effectiveDate)}</td>
                    <td className="py-2 pr-4">{record.notes || '—'}</td>
                    <td className="py-2 pr-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(record)}
                        title="Remove this calibration"
                        aria-label={`Remove ${record.parameter} calibration for sensor ${record.serialNumber}`}
                        className="material-icons-outlined text-base text-gray-400 hover:text-error-500"
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
